import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiFillFileAdd } from 'react-icons/ai'
import { MdFilePresent } from 'react-icons/md'
import { RiCloseFill } from 'react-icons/ri'

import AttachFileIcon from '@mui/icons-material/AttachFile'
import { Box, IconButton } from '@mui/material'
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks'

import {
  BG_DARK_GRAY,
  PRIMARY_GREEN,
  PRIMARY_RED,
  PRIMARY_YELLOW,
  PURE_BLACK,
  THEME_GRAY,
} from 'utils/customizedTheme'

interface IProps {
  fieldProps: { name: string; required?: boolean }
  fileData?: { key: string; value: any }[]
  savedAttachmentKey: string
  values: any
  errors: any
  setFieldValue: any
  disabled?: boolean
  id: string
  label: string,
  formSubmitCount: number,
  saveAttachmentApiCall: MutationTrigger<any>
  deleteAttachmentApiCall: MutationTrigger<any>
}

const SingleAttachmentButton: FC<IProps> = ({
  fieldProps,
  id,
  label,
  formSubmitCount = 0,
  savedAttachmentKey,
  values,
  errors,
  setFieldValue,
  disabled,
  saveAttachmentApiCall,
  deleteAttachmentApiCall,
}) => {
  const { t } = useTranslation()

  const [file, setFile] = useState(null)

  const addFile = (e: any) => {
    const selectedFile = e?.currentTarget?.files?.item(0) || null
    setFile(selectedFile);
  }

  // Upload files after form is saved
  useEffect(() => {
    if (formSubmitCount>0 && formSubmitCount<10) {

      if (file) {
        const constructedFormData = () => {
          const formData = new FormData()
          formData.append(savedAttachmentKey, file)
          return formData
        }

        const data = constructedFormData();

        if(id)
          saveAttachmentApiCall({ id, formData: data })
        else{
          saveAttachmentApiCall({ formData: data })
        }
      }
    }
  }, [id, formSubmitCount])


  return (
    <>
      <Box className="transparent-background"
        sx={{
          '& a:hover': { color: THEME_GRAY + ' !important' },
        }}
      >
        <input
          name={fieldProps.name}
          type='file'
          id='attachments'
          style={{ display: 'none' }}
          onChange={(e: any) => addFile(e)}
        />
        <label
          htmlFor='attachments'
          className='hover-blue'
          style={{
            border: '1px solid white',
            borderRadius: '0.8rem',
            padding: '0 0.5rem 0.8rem 1rem',
            position: 'relative',
            cursor: 'pointer',
            display: 'inline-block',
            lineHeight: 1,
            pointerEvents: disabled ? 'none' : 'auto',
            background: disabled ? BG_DARK_GRAY : PRIMARY_YELLOW,
          }}
        >
          <>
            <span
              style={{
                display: 'inline-block',
                lineHeight: '1',
                margin: '0.55rem 0.2rem 0',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
              <AttachFileIcon sx={{ margin: '0 0 -0.35rem 0.2rem' }} />
            </span>{' '}
            {fieldProps?.required && (
              <span
                style={{
                  color: PRIMARY_RED,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  position: 'absolute',
                  top: '-0.5rem',
                  right: '-0.8rem',
                }}
              >
                *
              </span>
            )}
          </>
        </label>
        <span
          style={{
            // fontWeight: 'bold',
            fontSize: '1rem',
            lineHeight: '1.66',
            textAlign: 'left',
            marginTop: '1rem',
            marginLeft: '1rem',
            wordBreak: 'break-all',
          }}
        >
          <ul
            style={{
              listStyle: 'none',
              paddingLeft: '0.5rem',
              marginBottom: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {file &&
                <li
                  key={`new_${savedAttachmentKey}`}
                  style={{
                    color: PRIMARY_GREEN,
                    minHeight: '2.3rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    wordBreak: 'break-all',
                    flexShrink: 0,
                  }}
                >
                  <>
                    <AiFillFileAdd
                      style={{ margin: '-0.2rem 0.5rem 0 0', fontSize: '1.25rem', color: PRIMARY_GREEN }}
                    />
                    {file.name}
                    <RemoveButton
                      entityId={file?.id}
                      disabled={disabled}
                      itemType='new'
                      values={values}
                      setFile={setFile}
                      setFieldValue={setFieldValue}
                      savedAttachmentKey={savedAttachmentKey}
                      deleteAttachmentApiCall={deleteAttachmentApiCall}
                    />
                  </>
                </li>
              }
            {values?.[savedAttachmentKey] &&
                <li
                  key={`old_${savedAttachmentKey}`}
                  style={{
                    minHeight: '2.3rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {
                        <a
                          href={`${values?.[savedAttachmentKey]}`}
                          target='_blank'
                          rel='noreferrer'
                          style={{ color: PURE_BLACK, textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          <MdFilePresent style={{ margin: '0 0.5rem -0.25rem 0', fontSize: '1.25rem' }} />
                          {values?.[savedAttachmentKey]}
                          <RemoveButton
                            entityId={id}
                            disabled={disabled}
                            itemType='old'
                            values={values}
                            setFile={setFile}
                            setFieldValue={setFieldValue}
                            savedAttachmentKey={savedAttachmentKey}
                            deleteAttachmentApiCall={deleteAttachmentApiCall}
                          />
                        </a>
                      }
                </li>
              }
          </ul>
        </span>
      </Box>

      {errors?.attachments && (
        <p
          style={{
            color: PRIMARY_RED,
            fontSize: '0.75rem',
            lineHeight: '1.66',
            textAlign: 'left',
            marginTop: '0.5rem',
            marginLeft: '1rem',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
          }}
        >
          {errors.attachments as string}
        </p>
      )}
    </>
  )
}
export default SingleAttachmentButton

interface IRemoveProps {
  itemType: 'old' | 'new'
  values: any
  setFieldValue: any
  setFile: any
  disabled?: boolean
  entityId: string
  savedAttachmentKey: string,
  deleteAttachmentApiCall: any
}

const RemoveButton: FC<IRemoveProps> = ({
  itemType,
  values,
  setFile,
  setFieldValue,
  disabled = false,
  entityId,
  savedAttachmentKey,
  deleteAttachmentApiCall
}) => {
  const handleRemove = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
  
   
    if (itemType === 'new') {
      setFile(null)
    }

    if (itemType === 'old') {
      setFile(null)
      setFieldValue(savedAttachmentKey, null)
      if(entityId)
        deleteAttachmentApiCall(entityId)
      else
        deleteAttachmentApiCall()
    }
  }

  return (
    <>
      {!disabled && (
        <IconButton
          color='error'
          className='remove-btn'
          onClick={handleRemove}
          style={{
            display: 'inline-flex',
            marginLeft: '0.3rem',
            marginTop: '-0.2rem',
            padding: '0.3rem',
          }}
        >
          <RiCloseFill style={{ fontSize: '1.4rem', fontWeight: '700', color: PRIMARY_RED }} />
        </IconButton>
      )}
    </>
  )
}
